import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money, StatusBadge } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";

export default function Inventory() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/admin/inventory").then((r) => setData(r.data));
    api.get("/admin/sync-logs").then((r) => setLogs(r.data));
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/inventory/sync"); toast.success(`Synced ${data.products_checked} products`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSyncing(false); }
  };

  if (!data) return <div className="animate-pulse h-64 bg-zinc-100 rounded-xl" />;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Inventory</h1><p className="text-sm text-zinc-500">Supplier stock sync & alerts</p></div>
        <Button onClick={sync} disabled={syncing} data-testid="sync-inventory-btn">
          {syncing ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RefreshCw size={15} className="mr-2" />}Sync Now
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Total Products</p><p className="text-2xl font-bold mt-1">{data.products.length}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Low Stock (≤10)</p><p className="text-2xl font-bold mt-1 text-amber-600">{data.low_stock_count}</p></div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">Out of Stock</p><p className="text-2xl font-bold mt-1 text-red-600">{data.out_of_stock_count}</p></div>
      </div>

      {data.low_stock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle size={15} />Low Stock Alerts</p>
          <div className="flex flex-wrap gap-2">
            {data.low_stock.map((p) => <span key={p.id} className="text-xs bg-white px-2.5 py-1 rounded-full">{p.title} ({p.stock})</span>)}
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 bg-zinc-50 border-b border-zinc-200">
            <th className="p-3 font-medium">Product</th><th className="p-3 font-medium">SKU</th>
            <th className="p-3 font-medium">Warehouse</th><th className="p-3 font-medium">Stock</th>
            <th className="p-3 font-medium">Supplier Price</th><th className="p-3 font-medium">Status</th><th className="p-3 font-medium">Last Sync</th>
          </tr></thead>
          <tbody>
            {data.products.slice(0, 50).map((p) => (
              <tr key={p.id} className="border-b border-zinc-50">
                <td className="p-3 font-medium line-clamp-1 max-w-[200px]">{p.title}</td>
                <td className="p-3 font-mono text-xs text-zinc-500">{p.internal_sku}</td>
                <td className="p-3 text-xs">{p.warehouse}</td>
                <td className={`p-3 font-medium ${p.stock <= 0 ? "text-red-600" : p.stock <= 10 ? "text-amber-600" : ""}`}>{p.stock}</td>
                <td className="p-3 font-mono text-xs">{money(p.supplier_price)}</td>
                <td className="p-3"><StatusBadge status={p.status} /></td>
                <td className="p-3 text-xs text-zinc-400">{p.last_sync_date ? new Date(p.last_sync_date).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {logs.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-3">Sync Logs</h3>
          {logs.slice(0, 8).map((l) => (
            <div key={l.id} className="flex justify-between text-xs py-1.5 border-b border-zinc-50 last:border-0">
              <span className="capitalize">{l.type.replace(/_/g, " ")} · {l.changes} changes</span>
              <span className="text-zinc-400">{new Date(l.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
